import { shallowEqual, useSelector } from 'react-redux'

import {
    makeResetFilters,
    makeResetLocalFilters,
    makeUpdateFilter,
    makeUpdateFilterSettings,
    makeUpdateLocalFilter,
} from 'actions/ui/shared/filter'
import { useDeepCompareMemo } from 'hooks/useDeepCompareMemo'
import { CobaltPath } from 'routing/Path'
import {
    selectFilters,
    selectFilterSettings,
    selectLocalFilters,
} from 'selectors/ui'
import {
    FilterSettings,
    FiltersState,
    FilterValue,
    RootReduxState,
} from 'types'

import { useAction } from './useAction'

interface HookOptions {
    pagePath: CobaltPath
    tabPath?: CobaltPath
}

interface UseFilterState {
    pageFilters: FiltersState
    pageLocalFilters: FiltersState
    tabFilters: FiltersState
    tabLocalFilters: FiltersState
    filterSettings: FilterSettings
}

export const useFiltersState = ({
    pagePath,
    tabPath,
}: HookOptions): UseFilterState => ({
    pageFilters: useSelector<RootReduxState, FiltersState>(
        (state: RootReduxState) => selectFilters(state, pagePath),
        shallowEqual
    ),
    pageLocalFilters: useSelector<RootReduxState, FiltersState>(
        (state: RootReduxState) => selectLocalFilters(state, pagePath),
        shallowEqual
    ),
    tabFilters: useSelector<RootReduxState, FiltersState>(
        (state) => (tabPath ? selectFilters(state, tabPath) : {}),
        shallowEqual
    ),
    tabLocalFilters: useSelector<RootReduxState, FiltersState>(
        (state: RootReduxState) =>
            tabPath ? selectLocalFilters(state, tabPath) : {},
        shallowEqual
    ),
    filterSettings: useSelector<RootReduxState, FilterSettings>(
        (state: RootReduxState) =>
            selectFilterSettings(state, tabPath || pagePath),
        shallowEqual
    ),
})

export type UpdateOptions = {
    key: string
    value?: FilterValue | number
    args?: any[]
    // a way to tell if the filter was updated by the user or
    // if it was updated automatically by the system. So FilterGroup
    // can set expectations accordingly
    automaticallyMade?: boolean
}

interface FilterDispatch {
    updatePageFilter: (options: UpdateOptions) => void
    updatePageLocalFilter: (options: UpdateOptions) => void
    updateTabFilter: (options: UpdateOptions) => void
    updateTabLocalFilter: (options: UpdateOptions) => void
    updateFilterSettings: (settings: FilterSettings) => void
    resetFilters: () => void
    resetLocalFilters: () => void
}

export const useFiltersDispatch = ({
    pagePath,
    tabPath,
}: HookOptions): FilterDispatch => {
    const updatePageFilterAction = useDeepCompareMemo(
        () => makeUpdateFilter(pagePath),
        [pagePath]
    )
    const updatePageLocalFilterAction = useDeepCompareMemo(
        () => makeUpdateLocalFilter(pagePath),
        [pagePath]
    )
    const updateTabFilterAction = useDeepCompareMemo(
        () => makeUpdateFilter(tabPath),
        [tabPath]
    )
    const updateTabLocalFilterAction = useDeepCompareMemo(
        () => makeUpdateLocalFilter(tabPath),
        [tabPath]
    )
    const updateFilterSettingsAction = useDeepCompareMemo(
        () => makeUpdateFilterSettings(tabPath || pagePath),
        [tabPath, pagePath]
    )
    const resetFiltersAction = useDeepCompareMemo(
        () => makeResetFilters(tabPath || pagePath),
        [tabPath, pagePath]
    )
    const resetLocalFiltersAction = useDeepCompareMemo(
        () => makeResetLocalFilters(tabPath || pagePath),
        [tabPath, pagePath]
    )

    return {
        updatePageFilter: useAction(updatePageFilterAction),
        updatePageLocalFilter: useAction(updatePageLocalFilterAction),
        updateTabFilter: useAction(updateTabFilterAction),
        updateTabLocalFilter: useAction(updateTabLocalFilterAction),
        updateFilterSettings: useAction(updateFilterSettingsAction),
        resetFilters: useAction(resetFiltersAction),
        resetLocalFilters: useAction(resetLocalFiltersAction),
    }
}
